import React, { useEffect } from 'react';
import * as d3 from 'd3';
import labeler from './labeler';
import { ImpactDestinationsWrapper } from './style';
import { useWindowResize } from '../../../../../hooks';

// Configurable Constants
const MAX_LINE_LENGTH = 100; 
const MIN_LINE_LENGTH = 30;
const COLLISION_PADDING_X = 20; 
const COLLISION_PADDING_Y = 30; 
const LABEL_GAP = 6; // Strict 6px distance from text

const D3BubbleGraph = (props) => {
  const {
    noDataInFilters,
    id,
    isTriangulation,
    type,
    graphDetails,
    tabChanged,
    domainValues,
    data,
    legends,
  } = props;

  let scalingValX = isTriangulation ? 4 : 1.5;

  const getColorForSentiments = (sentiment, shade = 'light') => {
    const isPos = sentiment === 'Positive';
    const isNeg = sentiment === 'Negative';
    if (shade === 'light') {
      if (isTriangulation) return legends.find((f) => f.value === sentiment).colorLight;
      return isPos ? 'rgba(0, 176, 80, 0.4)' : isNeg ? 'rgba(255, 0, 0, 0.4)' : 'rgba(166, 166, 166, 0.4)';
    } else {
      if (isTriangulation) return legends.find((f) => f.value === sentiment).colorDark;
      return isPos ? 'rgba(0, 176, 80, 1)' : isNeg ? 'rgba(255, 0, 0, 1)' : 'rgba(166, 166, 166, 1)';
    }
  };

  const onLoadFunc = () => {
    const idWrapper = document.getElementById(id);
    if (!idWrapper) return;
    idWrapper.innerHTML = '';

    const margin = { top: 0, right: 0, bottom: 50, left: 0 };
    let width = idWrapper.offsetWidth - margin.left - margin.right;
    let height = 500 - margin.top - margin.bottom;

    let labelArray = [], anchorArray = [];

    const xScale = d3.scaleLinear()
      .domain([
        domainValues.xMin - (tabChanged === 0 ? scalingValX : 0),
        domainValues.xMax + (tabChanged === 0 ? scalingValX : 0),
      ])
      .range([0, width]);

    const yScale = d3.scaleLinear().domain([domainValues.yMin, domainValues.yMax]).range([height, 0]);
    const rScale = d3.scaleSqrt().domain([5, 100]).range([15, 30]);

    const svg = d3.select(`#${id}`)
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .style('overflow', 'visible')
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    const elementClickShowPopupFunc = (events, dObject) => {
      const thsWrapperId = idWrapper.parentNode;
      const popupId = `${type}_popup`;

      if (dObject?.Insights) {
        const wrapperDetails = idWrapper.getBoundingClientRect();
        const position = {
          x: events.clientX - (window.scrollX + wrapperDetails.left),
          y: events.clientY - (window.scrollY + wrapperDetails.top),
        };
        const content = `<div class='overlay' id='popupClose'></div><div class='popup' id='${popupId}' style='top:${position.y}px; left:${position.x}px; max-height:${wrapperDetails.height - 20}px; overflow-y:auto'><a class='close' href='javascript:' id='close'><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1 1L9 9" stroke="#C3C3C3" stroke-width="1.5" stroke-linecap="round"/><path d="M9 1L1 9" stroke="#C3C3C3" stroke-width="1.5" stroke-linecap="round"/></svg></a><p class='driverName'>${dObject.DriverName}</p><div class='description'>${dObject?.Insights}</div></div>`;
        thsWrapperId.querySelector('.tooltip').innerHTML = content;
        thsWrapperId.querySelector('a#close').addEventListener('click', () => thsWrapperId.querySelector('.tooltip').innerHTML = '');
        thsWrapperId.querySelector('div#popupClose').addEventListener('click', () => thsWrapperId.querySelector('.tooltip').innerHTML = '');
      }
    };

    data.forEach((d, i) => {
      const xPos = xScale(d.BuzzVolumeIndex);
      const yPos = yScale(d.MeanRatingIndex);
      const r = rScale(d.PercentEngagement);
      labelArray.push({
        x: xPos, y: yPos, name: d.DriverName,
        color: getColorForSentiments(d.Sentiment, 'dark'),
        actualR: r, index: i,
      });
      anchorArray.push({ x: xPos, y: yPos, r: r + 5 });
    });

    const connectors = svg.selectAll('.connector').data(labelArray).enter().append('polyline')
      .attr('class', (d, i) => `connector connector-${i}`)
      .attr('fill', 'none').attr('stroke', (d) => d.color).attr('stroke-width', 1.2)
      .style('opacity', 0.7); 

    const bubbles = svg.selectAll('.bubble-group').data(data).enter().append('g')
      .attr('class', (d, i) => `bubble-group bubble-group-${i}`)
      .attr('transform', (d) => `translate(${xScale(d.BuzzVolumeIndex)}, ${yScale(d.MeanRatingIndex)})`)
      .style('cursor', 'pointer')
      .style('opacity', 0.7) 
      .on('click', (event, d) => elementClickShowPopupFunc(event, d))
      .on('mouseover', function (event, d) {
        const idx = data.indexOf(d);
        svg.select(`.label-text-${idx}`).style('font-weight', 'bold');
        svg.select(`.connector-${idx}`).style('opacity', 1);
        d3.select(this).select('.outer-ring');
        d3.select(this).style('opacity', 1);
      })
      .on('mouseout', function (event, d) {
        const idx = data.indexOf(d);
        const col = getColorForSentiments(d.Sentiment, 'dark');
        svg.select(`.label-text-${idx}`).style('font-weight', 'normal');
        svg.select(`.connector-${idx}`).style('opacity', 0.7);
        d3.select(this).select('.outer-ring');
        d3.select(this).style('opacity', 0.7);
      });

    bubbles.append('circle').attr('class', 'outer-ring').attr('r', (d) => rScale(d.PercentEngagement))
      .attr('fill', '#fff').attr('stroke', (d) => getColorForSentiments(d.Sentiment, 'dark')).attr('stroke-width', 1.5);

    bubbles.append('circle').attr('class', 'inner-circle').attr('r', (d) => rScale(d.PercentEngagement) * 0.7)
      .attr('fill', (d) => getColorForSentiments(d.Sentiment, 'light'));

    const labels = svg.selectAll('.label').data(labelArray).enter().append('text')
      .attr('class', (d, i) => `label label-text-${i}`)
      .text((d) => d.name)
      .attr('font-size', '12px').attr('font-family', 'Arial, sans-serif').attr('alignment-baseline', 'middle')
      .style('cursor', 'pointer')
      .on('click', (event, d) => {
        const dObject = data.find((f) => f.DriverName === d.name);
        elementClickShowPopupFunc(event, dObject);
      })
      .on('mouseover', function (event, d) {
        const idx = d.index;
        d3.select(this).style('font-weight', 'bold');
        svg.select(`.connector-${idx}`).style('opacity', 1);
        svg.select(`.bubble-group-${idx}`).style('opacity', 1);
      })
      .on('mouseout', function (event, d) {
        const idx = d.index;
        d3.select(this).style('font-weight', 'normal');
        svg.select(`.connector-${idx}`).style('opacity', 0.7);
        svg.select(`.bubble-group-${idx}`).style('opacity', 0.7);
      });

    labels.each(function (d, i) {
      const bbox = this.getBBox();
      labelArray[i].width = bbox.width + COLLISION_PADDING_X; 
      labelArray[i].height = bbox.height + COLLISION_PADDING_Y;
      labelArray[i].trueWidth = bbox.width;
      labelArray[i].trueHeight = bbox.height;
    });

    labeler().label(labelArray).anchor(anchorArray).width(width).height(height).start(8000); 

    const svgPadding = 10;
    labelArray.forEach((d, i) => {
      const anchor = anchorArray[i];
      let dx = d.x - anchor.x;
      let dy = d.y - anchor.y;
      let dist = Math.sqrt(dx * Math.sqrt(dx * dx + dy * dy));
      let lineLen = Math.sqrt(dx * dx + dy * dy) - d.actualR;

      if (lineLen < MIN_LINE_LENGTH || lineLen > MAX_LINE_LENGTH) {
        const angle = Math.atan2(dy, dx);
        const clampedLen = Math.max(MIN_LINE_LENGTH, Math.min(lineLen, MAX_LINE_LENGTH));
        const finalDist = d.actualR + clampedLen;
        d.x = anchor.x + finalDist * Math.cos(angle);
        d.y = anchor.y + finalDist * Math.sin(angle);
      }

      d.x = Math.max(svgPadding, Math.min(d.x, width - d.trueWidth - svgPadding));
      d.y = Math.max(d.trueHeight + svgPadding, Math.min(d.y, height - svgPadding));
    });

    // --- Strict Dynamic Anti-Overlap Loop Pass ---
    for (let iter = 0; iter < 20; iter++) {
      let positionUpdated = false;

      labelArray.forEach((d, i) => {
        const rW = d.trueWidth;
        const rH = d.trueHeight;

        anchorArray.forEach((anchor) => {
          const cx = anchor.x;
          const cy = anchor.y;
          const cr = anchor.r + 5; 

          const rx1 = d.x;
          const rx2 = d.x + rW;
          const ry1 = d.y - rH / 2; 
          const ry2 = d.y + rH / 2;

          const closestX = Math.max(rx1, Math.min(cx, rx2));
          const closestY = Math.max(ry1, Math.min(cy, ry2));

          const distX = cx - closestX;
          const distY = cy - closestY;
          const distanceSquared = distX * distX + distY * distY;

          if (distanceSquared < cr * cr) {
            const currentDist = Math.sqrt(distanceSquared) || 1;
            const pushLength = cr - currentDist + 3;

            const labelCenterX = d.x + rW / 2;
            const labelCenterY = d.y;
            let vectorX = labelCenterX - cx;
            let vectorY = labelCenterY - cy;
            if (vectorX === 0 && vectorY === 0) vectorX = 1;

            const length = Math.sqrt(vectorX * vectorX + vectorY * vectorY) || 1;

            d.x += (vectorX / length) * pushLength;
            d.y += (vectorY / length) * pushLength;
            positionUpdated = true;
          }
        });

        for (let j = 0; j < labelArray.length; j++) {
          if (i === j) continue;
          const o = labelArray[j];

          const text1_x1 = d.x, text1_x2 = d.x + rW, text1_y1 = d.y - rH / 2, text1_y2 = d.y + rH / 2;
          const text2_x1 = o.x, text2_x2 = o.x + o.trueWidth, text2_y1 = o.y - o.trueHeight / 2, text2_y2 = o.y + o.trueHeight / 2;

          const bufferHorizontal = 12; 
          const bufferVertical = 6;   

          if (text1_x1 < text2_x2 + bufferHorizontal && text1_x2 + bufferHorizontal > text2_x1 && 
              text1_y1 < text2_y2 + bufferVertical && text1_y2 + bufferVertical > text2_y1) {
            
            const overlapX = Math.min(text1_x2 - text2_x1, text2_x2 - text1_x1) + bufferHorizontal;
            const overlapY = Math.min(text1_y2 - text2_y1, text2_y2 - text1_y1) + bufferVertical;

            if (overlapX < overlapY) {
              if ((text1_x1 + text1_x2) / 2 < (text2_x1 + text2_x2) / 2) {
                d.x -= overlapX * 0.5;
              } else {
                d.x += overlapX * 0.5;
              }
            } else {
              if (d.y < o.y) {
                d.y -= overlapY * 0.5;
              } else {
                d.y += overlapY * 0.5;
              }
            }
            positionUpdated = true;
          }
        }

        d.x = Math.max(svgPadding, Math.min(d.x, width - d.trueWidth - svgPadding));
        d.y = Math.max(d.trueHeight + svgPadding, Math.min(d.y, height - svgPadding));
      });

      if (!positionUpdated) break; 
    }

    labels.attr('x', (d) => d.x).attr('y', (d) => d.y);

    connectors.attr('points', (d, i) => {
      const anchor = anchorArray[i];
      const distToStart = Math.abs(anchor.x - d.x);
      const distToEnd = Math.abs(anchor.x - (d.x + d.trueWidth));
      const isNearestToStart = distToStart < distToEnd;

      const xTarget = isNearestToStart ? (d.x - LABEL_GAP) : (d.x + d.trueWidth + LABEL_GAP);
      const yTarget = d.y;

      const angle = Math.atan2(yTarget - anchor.y, xTarget - anchor.x);
      const xStart = anchor.x + d.actualR * Math.cos(angle);
      const yStart = anchor.y + d.actualR * Math.sin(angle);
      
      let midX = isNearestToStart ? Math.min(xStart + 10, xTarget - 4) : Math.max(xStart - 10, xTarget + 4);
      
      // If horizontal line segment drops below 10px, find the mathematical nearest point on the padded text bounding box
      if (Math.abs(xTarget - midX) < 10) {
        // Define text bounding coordinates inclusive of 10px padding on all sides
        const padLeft = d.x - 2;
        const padRight = d.x + d.trueWidth + 2;
        const padTop = d.y - (d.trueHeight / 2) - 2;
        const padBottom = d.y + (d.trueHeight / 2) + 2;

        // Clamp circle coordinate against the padded rectangle boundaries to automatically map the exact closest landing spot
        const xTargetStraight = Math.max(padLeft, Math.min(anchor.x, padRight));
        const yTargetStraight = Math.max(padTop, Math.min(anchor.y, padBottom));

        // Re-align line starting vector cleanly matching this target coordinate
        const angleStraight = Math.atan2(yTargetStraight - anchor.y, xTargetStraight - anchor.x);
        const xStartStraight = anchor.x + d.actualR * Math.cos(angleStraight);
        const yStartStraight = anchor.y + d.actualR * Math.sin(angleStraight);
        
        return `${xStartStraight},${yStartStraight} ${xTargetStraight},${yTargetStraight}`;
      }
      
      return `${xStart},${yStart} ${midX},${yTarget} ${xTarget},${yTarget}`;
    });

    svg.append('line').attr('x1', 0).attr('y1', yScale(graphDetails.AxisCutHorizontal)).attr('x2', width).attr('y2', yScale(graphDetails.AxisCutHorizontal)).attr('stroke', 'rgba(126, 126, 126, 0.5)').attr('stroke-width', 1).attr('stroke-dasharray', '4,4');
    svg.append('line').attr('x1', xScale(graphDetails.AxisCutVertical)).attr('y1', 0).attr('x2', xScale(graphDetails.AxisCutVertical)).attr('y2', height).attr('stroke', 'rgba(126, 126, 126, 0.5)').attr('stroke-width', 1).attr('stroke-dasharray', '4,4');
  };

  useEffect(() => {
    onLoadFunc();
  }, [data, tabChanged]);

  useWindowResize(onLoadFunc);

  return (
    <ImpactDestinationsWrapper id={`${id}_Wrapper`} className={noDataInFilters ? 'noDataInFilters_true' : ''}>
      <div className="svgWrapper" id={`${id}`}></div>
      <div className="columnLine"><span className='new'>{graphDetails.AxisNameVertical}</span></div>
      <div className="rowLine"><span>{graphDetails.AxisNameHorizontal}</span></div>
      <div className="tooltip"></div>
    </ImpactDestinationsWrapper>
  );
};

export default D3BubbleGraph;